import React, { useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import store from '../stores/NotesStore';
import { findBestMetadataMatch } from '../helpers/metadataHelpers';
import MetadataLinkModal from './MetadataLinkModal';
import { fmtDate, fmt } from '../helpers/helpers';
import './SuggestionsPanel.css';

const SuggestionsPanel = observer(() => {
  const [collapsed, setCollapsed] = useState(false);
  const [metadataLinkEntry, setMetadataLinkEntry] = useState(null);

  const suggestions = store.suggestions;

  // Vincular candidates: current note's entries without metadataId that fuzzy-match
  // a suggestion name (either kind — derived from the entry's sign)
  const allMetadata = Array.from(store.metadata.values());
  const vincularCandidates = useMemo(() => {
    if (!store.entries || suggestions.length === 0) return [];
    const results = [];
    for (const entry of store.entries) {
      if (entry.metadataId) continue;
      const label = entry.label || entry.customName || '';
      if (!label.trim()) continue;
      const kind = entry.value < 0 ? 'expense' : 'income';
      const match = findBestMetadataMatch(label, allMetadata, kind, 0.7);
      if (!match) continue;
      // Only show if this metadataId has a suggestion (from previous month)
      const hasSuggestion = suggestions.some(s => s.metadataId === match.id);
      if (!hasSuggestion) continue;
      // Don't show if already in alreadyAdded (entry is already linked)
      results.push({ entry, match });
    }
    return results;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.entries, allMetadata, suggestions]);

  // Hidden for guests or when no suggestions
  if (store.isGuest || suggestions.length === 0) return null;

  // Já adicionados: in current note, with a suggestedDueDate that differs from currentDueDate
  const alreadyAdded = suggestions.filter(
    s => s.isInCurrentNote && s.suggestedDueDate && s.currentDueDate !== s.suggestedDueDate
  );

  // Esperados: not in current note yet
  const expected = suggestions.filter(s => !s.isInCurrentNote);
  const expectedExpenses = expected.filter(s => s.kind !== 'income');
  const expectedIncome = expected.filter(s => s.kind === 'income');

  const totalCount = alreadyAdded.length + expected.length;
  if (totalCount === 0 && vincularCandidates.length === 0) return null;

  return (
    <div className="suggestions-panel">
      <div className="suggestions-header" onClick={() => setCollapsed(c => !c)}>
        <span className="suggestions-title">
          <i className="fa-solid fa-rotate"></i>
          Recorrentes
          <span className="suggestions-count">{totalCount + vincularCandidates.length}</span>
        </span>
        <i className={`fa-solid fa-chevron-${collapsed ? 'right' : 'down'} suggestions-chevron`}></i>
      </div>

      {!collapsed && (
        <div className="suggestions-body">
          {alreadyAdded.length > 0 && (
            <section className="suggestions-section">
              <div className="suggestions-section-title">Já adicionados — data sugerida</div>
              {alreadyAdded.map(s => (
                <div key={s.metadataId} className="suggestion-row suggestion-row--matched">
                  <span className="suggestion-name">{s.metadataName}</span>
                  <span className="suggestion-arrow">→</span>
                  <span className="suggestion-date">{fmtDate(s.suggestedDueDate)}</span>
                  <button
                    className="suggestion-btn"
                    onClick={() => store.changeDueDate({ id: s.currentEntryId }, s.suggestedDueDate)}
                    title="Aplicar data sugerida"
                  >
                    Usar data
                  </button>
                </div>
              ))}
            </section>
          )}

          {vincularCandidates.length > 0 && (
            <section className="suggestions-section">
              <div className="suggestions-section-title">Aguardando vínculo</div>
              {vincularCandidates.map(({ entry, match }) => (
                <div key={entry.id} className="suggestion-row suggestion-row--vincular">
                  <span className="suggestion-name">{entry.label || entry.customName}</span>
                  <span className="suggestion-arrow">≈</span>
                  <span className="suggestion-match-name">{match.name}</span>
                  <button
                    className="suggestion-btn suggestion-btn--vincular"
                    onClick={() => setMetadataLinkEntry(entry)}
                    title="Vincular ao cadastro"
                  >
                    Vincular
                  </button>
                </div>
              ))}
            </section>
          )}

          {expected.length > 0 && (
            <section className="suggestions-section">
              <div className="suggestions-section-title">Esperados — não encontrados neste mês</div>
              {expectedExpenses.map(s => (
                <div key={s.metadataId} className="suggestion-row suggestion-row--expected">
                  <span className="suggestion-name">{s.metadataName}</span>
                  {s.suggestedDueDate && (
                    <>
                      <span className="suggestion-arrow">→</span>
                      <span className="suggestion-date suggestion-date--muted">{fmtDate(s.suggestedDueDate)}</span>
                    </>
                  )}
                  <span className="suggestion-value suggestion-value--muted">{fmt(s.lastValue)}</span>
                  {!s.suggestedDueDate && (
                    <span className="suggestion-no-date">sem data</span>
                  )}
                  <button
                    className="suggestion-btn"
                    onClick={() => store.addSuggestionEntry(s)}
                    title="Adicionar ao mês atual"
                  >
                    Adicionar
                  </button>
                </div>
              ))}
              {expectedIncome.length > 0 && (
                <>
                  <div className="suggestions-section-title suggestions-section-title--sub">Receitas esperadas</div>
                  {expectedIncome.map(s => (
                    <div key={s.metadataId} className="suggestion-row suggestion-row--expected">
                      <span className="suggestion-name">{s.metadataName}</span>
                      {s.suggestedDueDate && (
                        <>
                          <span className="suggestion-arrow">→</span>
                          <span className="suggestion-date suggestion-date--muted">{fmtDate(s.suggestedDueDate)}</span>
                        </>
                      )}
                      <span className="suggestion-value suggestion-value--muted">{fmt(s.lastValue)}</span>
                      {!s.suggestedDueDate && (
                        <span className="suggestion-no-date">sem data</span>
                      )}
                      <button
                        className="suggestion-btn"
                        onClick={() => store.addSuggestionEntry(s)}
                        title="Adicionar ao mês atual"
                      >
                        Adicionar
                      </button>
                    </div>
                  ))}
                </>
              )}
            </section>
          )}
        </div>
      )}

      {metadataLinkEntry && (
        <MetadataLinkModal
          entry={metadataLinkEntry}
          onClose={() => setMetadataLinkEntry(null)}
        />
      )}
    </div>
  );
});

export default SuggestionsPanel;
